import React from 'react';
import { Link } from 'react-router-dom';
import {
    Factory, Utensils, Plane, Car, Cog, Zap, Anchor, Printer, Wrench, CircuitBoard,
} from 'lucide-react';
import CTASection from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const INDUSTRIES = [
    { icon: Plane, name: 'Aerospace', desc: 'Precision cleaning for turbine blades, fasteners, aircraft components — dry ice and laser cleaning without secondary waste.' },
    { icon: Car, name: 'Automotive', desc: 'Production line, assembly, weld prep, and paint shop cleaning for OEMs and tier-1 suppliers.' },
    { icon: Utensils, name: 'Food & Beverage', desc: 'USDA/FDA-friendly cleaning for ovens, conveyors, packaging, and processing equipment — no water, no residue.' },
    { icon: Factory, name: 'Manufacturing', desc: 'Machinery, robotics, molds, tooling, and production equipment — cleaned around your schedule.' },
    { icon: Cog, name: 'Injection Molding', desc: 'In-place mold cleaning with dry ice — remove carbon buildup and release agents without disassembly or damage.' },
    { icon: Zap, name: 'Power Generation', desc: 'Turbines, generators, switchgear, transformers — dry, non-conductive cleaning for energized components.' },
    { icon: Anchor, name: 'Marine', desc: 'Ship hulls, decks, engine rooms, and dock equipment — corrosion, coating, and biofouling removal.' },
    { icon: Printer, name: 'Printing', desc: 'Press cleaning without downtime — remove ink buildup from rollers, cylinders, and drying systems.' },
    { icon: Wrench, name: 'Plastics', desc: 'Injection and extrusion equipment — dies, screws, hot runners, cooling systems.' },
    { icon: CircuitBoard, name: 'Electrical & Electronics', desc: 'Switchgear, motors, PCBs, control panels — non-abrasive, non-conductive dry ice cleaning.' },
];

const Industries = () => {
    useSEO({
        title: 'Industries We Serve — Industrial Cleaning by Dry Ice Bulls Cleaning System®',
        description: 'Advanced industrial cleaning across aerospace, automotive, food & beverage, manufacturing, injection molding, power generation, marine, printing, plastics, and electronics.',
    });

    return (
        <div className="pt-24 pb-24" data-testid="industries-page">
            <div className="container-page">
                <div className="label-caps text-brand-primary mb-4">/ Industries We Serve</div>
                <h1 className="font-display font-black uppercase text-5xl md:text-7xl leading-[0.95] text-neutral-900">
                    Built for the plants,<br />
                    <span className="text-brand-primary">specified by the engineers.</span>
                </h1>
                <p className="mt-6 max-w-3xl text-base md:text-lg text-neutral-600 leading-relaxed">
                    Dry Ice Bulls Cleaning System® supports industrial operations across ten sectors — each with unique material, contamination, and downtime constraints. We select the right cleaning technology for the job.
                </p>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {INDUSTRIES.map((ind) => {
                        const Icon = ind.icon;
                        return (
                            <div
                                key={ind.name}
                                className="group border border-neutral-200 hover:border-brand-primary transition-colors bg-white p-6"
                                data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <div className="inline-flex items-center justify-center w-12 h-12 border border-neutral-200 group-hover:border-brand-primary group-hover:bg-brand-primary/5 transition-colors mb-4">
                                    <Icon className="h-6 w-6 text-brand-primary" />
                                </div>
                                <h3 className="font-display font-bold uppercase text-xl text-neutral-900 leading-tight">
                                    {ind.name}
                                </h3>
                                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                                    {ind.desc}
                                </p>
                            </div>
                        );
                    })}
                </div>

                <section className="mt-16">
                    <div className="max-w-3xl">
                        <div className="label-caps text-brand-primary mb-3">/ Not sure where you fit?</div>
                        <h2 className="font-display font-black uppercase text-3xl md:text-5xl text-neutral-900 leading-tight">
                            We&apos;ve probably cleaned it before.
                        </h2>
                        <p className="mt-4 text-base md:text-lg text-neutral-600 leading-relaxed">
                            Tell us what you clean and how often — we&apos;ll recommend the right dry ice blasting machine, nozzles and air setup for your industry.
                        </p>
                    </div>
                </section>
            </div>

            {/* Full-bleed conversion CTA */}
            <CTASection
                variant="services"
                heading="Serving your industry."
                subheading="From aerospace to food processing, we help engineering and maintenance teams pick the right cleaning technology for every material and downtime constraint."
                context={{ trackingLocation: 'industries_bottom' }}
                testid="industries-cta-bottom"
            />
        </div>
    );
};

export default Industries;
